import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as fabric from 'fabric';
import { apiGet, apiPost, apiPostForm, AuthError } from '../api';
import { useClient } from '../ClientContext';
import Icon from '../components/Icon';

/**
 * Agentic Studio.
 *
 * A canvas for building and amending paid social creative, with the client's
 * brand already loaded into it.
 *
 * WHY EVERY IMAGE ARRIVES AS BASE64
 * ---------------------------------
 * A canvas that has drawn an image fetched from another origin is tainted, and
 * toDataURL() on a tainted canvas throws a SecurityError. Signed GCS URLs are
 * another origin and carry no CORS headers, so a studio built on them would
 * work perfectly until someone pressed Save. Every image here therefore comes
 * through the API as bytes and becomes a data: URL, which never taints.
 *
 * LAYER ROLES
 * -----------
 * Fabric only knows an object is an "image". This page needs to know whether
 * it is the artwork or a logo, because amending the artwork must leave the logo
 * alone and generating a new picture must replace the old one rather than stack
 * behind it. Every object therefore carries a bsRole:
 *
 *     'artwork'  the picture. Replaced by generate and by amend.
 *     'logo'     a brand logo. Never sent to the model, never replaced.
 *     'text'     a headline or body line. Never sent to the model.
 *     'block'    a colour panel.
 *
 * Those roles are now shown as a layer list. They were the one fact the page
 * knew about the canvas that the person using it could not see — which made
 * "Front" and "Back" a guess, and a headline hidden behind a full-bleed image
 * impossible to find.
 *
 * WHAT THIS PAGE CANNOT DO
 * ------------------------
 * Publish. Save writes to the creative library as pending approval, exactly
 * like an upload. A human still approves before anything reaches Meta.
 */

// Placement sizes, not arbitrary numbers. Each is what the platform actually
// serves, so an asset built here needs no resizing afterwards.
const FORMATS = [
  { label: 'Square',    sub: 'Feed',        w: 1080, h: 1080, ratio: '1:1'  },
  { label: 'Portrait',  sub: 'Feed',        w: 1080, h: 1350, ratio: '4:5'  },
  { label: 'Story',     sub: 'Story / Reel', w: 1080, h: 1920, ratio: '9:16' },
  { label: 'Landscape', sub: 'Feed',        w: 1200, h: 628,  ratio: '1.91:1' },
];

// The canvas is displayed at a size that fits the page and exported at full
// resolution. Fabric works in display pixels, so everything is authored at
// this scale and multiplied on the way out.
const DISPLAY_MAX = 520;

// Somewhere to pick a colour from before a brand is configured. These are the
// dashboard's own tokens, so an unbranded asset still looks deliberate rather
// than defaulting to pure black on white.
const FALLBACK_PALETTE = [
  { name: 'Pine',  hex: '#0f2e24' },
  { name: 'Brass', hex: '#e8a33d' },
  { name: 'Paper', hex: '#f7f4ec' },
  { name: 'White', hex: '#ffffff' },
  { name: 'Ink',   hex: '#16161f' },
];

const FONT_WEIGHTS = [300, 400, 500, 600, 700, 800];

// Always-available faces, offered alongside anything uploaded under Brand
// Guidelines. Every one of these is either loaded by the dashboard's own
// stylesheet or present on the operating system.
//
// Space Mono used to be on this list, on the grounds that the stylesheet
// loaded it. The stylesheet no longer does — it was removed when the interface
// dropped monospace — so choosing it silently fell back to something else and
// the canvas quietly ignored the choice. An offer the page cannot honour is
// worse than no offer.
const SYSTEM_FONTS = ['Sora', 'Instrument Sans', 'Georgia', 'Helvetica', 'Arial'];

const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2];

// Work in progress is held per client, in sessionStorage rather than on the
// server. Leaving the page and coming back should not lose a layout, but an
// unsaved canvas is not a record of anything — the creative library is. Session
// scope means it survives navigation and does not outlive the browser tab.
const draftKey = (accountId) => `bidsense.studio.${accountId}`;

// A canvas carrying a full-size image serialises to megabytes, and
// sessionStorage rejects anything much past five. Rather than fail silently on
// save, the page checks first and says so.
const MAX_DRAFT_CHARS = 4_000_000;

// Custom properties Fabric does not know about, and would otherwise drop on
// serialisation — which would turn every layer back into an anonymous image
// and break amend, replace and undo all at once.
const SERIALISE_PROPS = ['bsRole'];

const ROLE_ICON = { artwork: 'image', logo: 'shield', text: 'text', block: 'square' };
const ROLE_NAME = { artwork: 'Image', logo: 'Logo', text: 'Text', block: 'Colour panel' };

function layerLabel(obj) {
  if (obj.bsRole === 'text') {
    const words = (obj.text || '').replace(/\s+/g, ' ').trim();
    return words ? (words.length > 26 ? `${words.slice(0, 26)}…` : words) : 'Text';
  }
  return ROLE_NAME[obj.bsRole] || 'Element';
}

/** The placement's own shape, drawn at its own ratio. */
function FormatGlyph({ w, h }) {
  const box = 15;
  const scale = box / Math.max(w, h);
  return (
    <span
      className="st-fmt-glyph"
      style={{ width: Math.max(5, Math.round(w * scale)), height: Math.max(5, Math.round(h * scale)) }}
      aria-hidden="true"
    />
  );
}

function Panel({ title, icon, tag, hint, children }) {
  return (
    <div className="st-panel">
      <div className="st-panel-h">
        {icon && <Icon name={icon} size={12} strokeWidth={2.2} />}
        {title}
        {tag && <span className="st-tag">{tag}</span>}
      </div>
      {hint && <div className="st-panel-hint">{hint}</div>}
      {children}
    </div>
  );
}

/** A labelled numeric field, compact enough for the properties rail. */
function NumField({ label, value, onChange, step = 1, min, max }) {
  return (
    <label className="st-field" style={{ marginBottom: 0 }}>
      <span className="st-field-l">{label}</span>
      <input
        className="form-input"
        type="number"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    </label>
  );
}

export default function AgenticStudio() {
  // Creative is Meta's: the studio saves to the Meta account whichever
  // platform is chosen elsewhere.
  const { metaAccountId: accountId, selected: client, isAll } = useClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const canvasElRef = useRef(null);
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  const [format, setFormat] = useState(FORMATS[0]);
  const [brand, setBrand] = useState({ guidelines: {}, logos: [], fonts: [] });
  const [library, setLibrary] = useState([]);

  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [prompt, setPrompt] = useState('');
  const [lastPrompt, setLastPrompt] = useState('');
  const [assetName, setAssetName] = useState('');
  const [campaignType, setCampaignType] = useState('OUTCOME_AWARENESS');

  // The selected object itself, not just its type — the properties rail reads
  // and writes its attributes directly.
  const [selected, setSelected] = useState(null);

  // Fabric mutates its objects in place, so changing a font size or a fill
  // leaves React with no idea anything happened and the rail keeps showing the
  // old value. The counter's value is never read; setting it is what forces the
  // re-render, which is why it is discarded here rather than held.
  const [, setTick] = useState(0);
  const bump = () => setTick((t) => t + 1);

  const [zoom, setZoom] = useState(1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftTooBig, setDraftTooBig] = useState(false);

  const fileInputRef = useRef(null);
  const loadedFromParam = useRef(false);

  // Undo is a stack of whole-canvas snapshots with a pointer, rather than a log
  // of reversible operations. Fabric offers no operation log, and a snapshot is
  // correct for every change without needing an inverse written for each one.
  // The cost is memory: each state carries every image as base64, so the depth
  // is deliberately shallow.
  const historyRef = useRef([]);
  const historyIndex = useRef(-1);
  const restoringRef = useRef(false);
  const pendingRestore = useRef(null);
  const readDraftOnce = useRef(false);

  // The canvas is built once per format and its event handlers close over
  // whatever existed then. Reaching the current functions through refs keeps
  // those handlers correct without rebuilding the canvas — which would throw
  // away the user's work every time an unrelated dependency changed.
  const snapshotRef = useRef(null);
  const persistRef = useRef(null);

  const scale = Math.min(DISPLAY_MAX / format.w, DISPLAY_MAX / format.h);
  const displayW = Math.round(format.w * scale);
  const displayH = Math.round(format.h * scale);

  const spacing = brand.guidelines?.spacing || {};
  const safeMarginPct = Number(spacing.safe_margin_pct) || 0;

  const palette = (brand.guidelines?.palette?.length
    ? brand.guidelines.palette
    : FALLBACK_PALETTE);
  const paletteIsBrand = Boolean(brand.guidelines?.palette?.length);

  // ── Canvas lifecycle ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!canvasElRef.current) return undefined;

    const canvas = new fabric.Canvas(canvasElRef.current, {
      width: displayW,
      height: displayH,
      backgroundColor: '#FFFFFF',
      preserveObjectStacking: true,
    });
    canvasRef.current = canvas;
    setReady(true);

    const sync = () => {
      setSelected(canvas.getActiveObject() || null);
      bump();
    };
    canvas.on('selection:created', sync);
    canvas.on('selection:updated', sync);
    canvas.on('selection:cleared', sync);
    canvas.on('object:modified', bump);

    // The layer list is rendered from the canvas, so adding or removing an
    // object has to reach React immediately — the debounced handler below is
    // for history and persistence, which can afford to wait.
    canvas.on('object:added', bump);
    canvas.on('object:removed', bump);

    // One handler for both jobs: every structural change is an undo point and a
    // reason to persist. Debounced, because dragging fires continuously and
    // neither the history nor sessionStorage wants a frame-by-frame record.
    let settle = null;
    const changed = () => {
      if (restoringRef.current) return;
      clearTimeout(settle);
      settle = setTimeout(() => { snapshotRef.current?.(); persistRef.current?.(); }, 400);
    };
    canvas.on('object:added', changed);
    canvas.on('object:modified', changed);
    canvas.on('object:removed', changed);

    return () => {
      clearTimeout(settle);
      canvas.dispose();
      canvasRef.current = null;
      setReady(false);
      setSelected(null);
    };
    // Rebuilding on format change is deliberate. Resizing a live canvas leaves
    // objects positioned for the old dimensions, and a story asset full of
    // furniture laid out for a square is worse than a clean canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayW, displayH]);

  /**
   * Select an object and tell React about it.
   *
   * Fabric fires selection:created for user clicks but not for
   * setActiveObject(), so a freshly added headline was selected on the canvas
   * and invisible to this component — which is why the delete control never
   * appeared for it.
   */
  const selectObject = useCallback((obj) => {
    const canvas = canvasRef.current;
    if (!canvas || !obj) return;
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();
    setSelected(obj);
    bump();
  }, []);

  const serialise = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return JSON.stringify(canvas.toObject(SERIALISE_PROPS));
  }, []);

  /** Record the current canvas as an undo point. */
  const snapshot = useCallback(() => {
    if (restoringRef.current) return;
    const state = serialise();
    if (state === null) return;

    // Anything after the pointer is a redo branch the user has just abandoned.
    historyRef.current = historyRef.current.slice(0, historyIndex.current + 1);
    if (historyRef.current[historyRef.current.length - 1] === state) return;

    historyRef.current.push(state);
    if (historyRef.current.length > 12) historyRef.current.shift();
    historyIndex.current = historyRef.current.length - 1;

    setCanUndo(historyIndex.current > 0);
    setCanRedo(false);
  }, [serialise]);

  const applyState = useCallback(async (state) => {
    const canvas = canvasRef.current;
    if (!canvas || !state) return;
    restoringRef.current = true;
    try {
      await canvas.loadFromJSON(state);
      canvas.renderAll();
    } catch (e) {
      setError('That step could not be restored.');
    }
    restoringRef.current = false;
    setSelected(null);
    bump();
  }, []);

  const undo = useCallback(async () => {
    if (historyIndex.current <= 0) return;
    historyIndex.current -= 1;
    await applyState(historyRef.current[historyIndex.current]);
    setCanUndo(historyIndex.current > 0);
    setCanRedo(historyIndex.current < historyRef.current.length - 1);
  }, [applyState]);

  const redo = useCallback(async () => {
    if (historyIndex.current >= historyRef.current.length - 1) return;
    historyIndex.current += 1;
    await applyState(historyRef.current[historyIndex.current]);
    setCanUndo(historyIndex.current > 0);
    setCanRedo(historyIndex.current < historyRef.current.length - 1);
  }, [applyState]);

  const removeObject = useCallback((obj) => {
    const canvas = canvasRef.current;
    if (!canvas || !obj) return;
    canvas.remove(obj);
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelected(null);
    bump();
  }, []);

  // Delete and Backspace remove the selection, except while a textbox is being
  // typed into — there, Backspace has to keep deleting characters.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const canvas = canvasRef.current;
      const active = canvas?.getActiveObject();
      if (!active || active.isEditing) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      e.preventDefault();
      removeObject(active);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [removeObject]);

  // ── Brand and library ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!accountId) return;
    setError(null);

    // inline=true so logos arrive as data URLs and can be placed on the canvas
    // without tainting it.
    apiGet(`/brand/${accountId}?inline=true`)
      .then(setBrand)
      .catch((err) => setError(
        err instanceof AuthError ? err.message
          : 'Could not load brand guidelines. The studio will open without them.'
      ));

    apiGet(`/creative-library/${accountId}`)
      .then((data) => setLibrary(data.assets || []))
      .catch(() => { /* the library picker is optional; a failure is not fatal */ });
  }, [accountId]);

  // Brand fonts are registered with the document so text drawn on the canvas is
  // set in the real typeface. Without this the family name resolves to nothing
  // and Fabric silently falls back, which looks like the brand being ignored.
  useEffect(() => {
    if (!brand.fonts?.length) return undefined;
    let cancelled = false;

    brand.fonts.forEach((font) => {
      if (!font.url) return;
      try {
        const face = new FontFace(font.label, `url(${font.url})`, {
          weight: font.variant || '400',
        });
        face.load()
          .then((loaded) => { if (!cancelled) document.fonts.add(loaded); })
          .catch(() => { /* an unloadable font falls back; not worth a banner */ });
      } catch (e) {
        // Malformed font file. The rest of the studio is unaffected.
      }
    });

    return () => { cancelled = true; };
  }, [brand.fonts]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const headingFamily = brand.guidelines?.typography?.heading?.family || 'Sora';
  const bodyFamily = brand.guidelines?.typography?.body?.family || 'Instrument Sans';

  // Brand faces first, then the always-available ones. Uploaded font files are
  // registered with the document under their label, so the label is the family
  // name to set on a textbox — no separate mapping to keep in step.
  const fontChoices = (() => {
    const out = [];
    const seen = new Set();
    const add = (value, label) => {
      const key = (value || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({ value: key, label: label || key });
    };
    (brand.fonts || []).forEach((f) => add(f.label, `${f.label} (uploaded)`));
    add(headingFamily, `${headingFamily} — heading`);
    add(bodyFamily, `${bodyFamily} — body`);
    SYSTEM_FONTS.forEach((f) => add(f));
    return out;
  })();

  const flash = (message) => { setNotice(message); setError(null); };

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  /**
   * Put an image in as the artwork layer, replacing whatever artwork is there.
   *
   * Replacing rather than adding is the fix for an amend that appeared to do
   * nothing: the new image went in behind the old one, so the canvas looked
   * unchanged until you dragged the original out of the way.
   */
  const setArtwork = useCallback(async (dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const img = await fabric.FabricImage.fromURL(dataUrl);
    img.bsRole = 'artwork';

    canvas.getObjects()
      .filter((o) => o.bsRole === 'artwork')
      .forEach((o) => canvas.remove(o));

    const cover = Math.max(displayW / img.width, displayH / img.height);
    img.set({
      originX: 'center', originY: 'center',
      left: displayW / 2, top: displayH / 2,
      scaleX: cover, scaleY: cover,
    });

    canvas.add(img);
    canvas.sendObjectToBack(img);
    canvas.requestRenderAll();
    bump();
    return img;
  }, [displayW, displayH]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const addText = (role) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const isHeading = role === 'heading';
    const inset = (safeMarginPct / 100) || 0.06;

    const text = new fabric.Textbox(isHeading ? 'Headline' : 'Supporting line', {
      left: displayW * inset,
      top: displayH * (isHeading ? 0.14 : 0.28),
      width: displayW * (1 - 2 * inset),
      fontSize: isHeading ? Math.round(displayH * 0.075) : Math.round(displayH * 0.04),
      fontFamily: isHeading ? headingFamily : bodyFamily,
      fontWeight: isHeading
        ? (brand.guidelines?.typography?.heading?.weight || 700)
        : (brand.guidelines?.typography?.body?.weight || 400),
      fill: palette[0]?.hex || '#0f2e24',
      lineHeight: 1.15,
      editable: true,
    });
    text.bsRole = 'text';
    canvas.add(text);
    selectObject(text);
  };

  const addLogo = async (logo) => {
    if (!logo.url) { setError('That logo file could not be read.'); return; }
    const canvas = canvasRef.current;
    if (!canvas) return;

    const img = await fabric.FabricImage.fromURL(logo.url);
    img.bsRole = 'logo';
    const clearPct = (Number(spacing.logo_clear_space_pct) || 4) / 100;
    img.scaleToWidth(displayW * 0.22);
    img.set({
      left: displayW * clearPct,
      top: displayH - (img.getScaledHeight() + displayH * clearPct),
    });
    canvas.add(img);
    selectObject(img);
  };

  const addColourBlock = (hex) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const radius = Number(spacing.corner_radius) ? Number(spacing.corner_radius) * scale : 0;
    const rect = new fabric.Rect({
      left: 0, top: displayH * 0.62,
      width: displayW, height: displayH * 0.38,
      fill: hex, rx: radius, ry: radius,
    });
    rect.bsRole = 'block';
    canvas.add(rect);
    selectObject(rect);
  };

  /** Apply a colour: to the selection if there is one, otherwise a new block. */
  const applyColour = (hex) => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (active && active.bsRole !== 'artwork' && active.bsRole !== 'logo') {
      active.set('fill', hex);
      canvas.requestRenderAll();
      bump();
    } else {
      addColourBlock(hex);
    }
  };

  const updateSelected = (props) => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!active) return;
    active.set(props);
    if (active.type === 'textbox') active.initDimensions?.();
    canvas.requestRenderAll();
    bump();
  };

  // ── Layer controls ─────────────────────────────────────────────────────────

  const raiseObject = (obj) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.bringObjectForward(obj);
    canvas.requestRenderAll();
    snapshot();
    bump();
  };

  const lowerObject = (obj) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.sendObjectBackwards(obj);
    canvas.requestRenderAll();
    snapshot();
    bump();
  };

  // Hiding rather than deleting, so a layer can be taken out of the picture and
  // brought back. A hidden layer is excluded from the export, which is the
  // whole point of being able to hide one.
  const toggleVisible = (obj) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    obj.set('visible', obj.visible === false);
    if (obj.visible === false && canvas.getActiveObject() === obj) {
      canvas.discardActiveObject();
      setSelected(null);
    }
    canvas.requestRenderAll();
    bump();
  };

  /**
   * Nudge the selection back inside the safe area.
   *
   * Works from the bounding rectangle and moves by a delta, rather than
   * clamping left and top. The artwork is positioned from its centre, so
   * clamping those as if they were the top-left corner drove it into the bottom
   * right — which is exactly what it did before this was rewritten.
   */
  const nudgeInsideSafeArea = () => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!active) return;

    const inset = (safeMarginPct || 6) / 100;
    const marginX = displayW * inset;
    const marginY = displayH * inset;
    const r = active.getBoundingRect();

    let dx = 0;
    let dy = 0;
    if (r.left < marginX) dx = marginX - r.left;
    else if (r.left + r.width > displayW - marginX) dx = (displayW - marginX) - (r.left + r.width);
    if (r.top < marginY) dy = marginY - r.top;
    else if (r.top + r.height > displayH - marginY) dy = (displayH - marginY) - (r.top + r.height);

    if (!dx && !dy) {
      flash('Already inside the safe area.');
      return;
    }

    active.set({ left: active.left + dx, top: active.top + dy });
    active.setCoords();
    canvas.requestRenderAll();
    snapshot();
    bump();
  };

  /** Hold the canvas for this client so navigating away does not lose it. */
  const persistDraft = useCallback(() => {
    if (!accountId) return;
    const state = serialise();
    if (!state) return;

    if (state.length > MAX_DRAFT_CHARS) {
      // Better to say so than to fail quietly and lose the layout on
      // navigation. The canvas still works; only its persistence does not.
      setDraftTooBig(true);
      setDraftSaved(false);
      return;
    }

    try {
      sessionStorage.setItem(draftKey(accountId), JSON.stringify({
        formatLabel: format.label,
        assetName,
        state,
      }));
      setDraftTooBig(false);
      setDraftSaved(true);
    } catch (e) {
      // Quota, private browsing, or storage disabled. Not worth interrupting.
      setDraftTooBig(true);
      setDraftSaved(false);
    }
  }, [accountId, format.label, assetName, serialise]);

  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);
  useEffect(() => { persistRef.current = persistDraft; }, [persistDraft]);

  // Read the held draft once per client, before the canvas exists. Setting the
  // format may rebuild the canvas, so the state waits in a ref and is applied
  // when the canvas is ready at the right size.
  useEffect(() => {
    if (!accountId || readDraftOnce.current) return;
    readDraftOnce.current = true;
    try {
      const raw = sessionStorage.getItem(draftKey(accountId));
      if (!raw) return;
      const saved = JSON.parse(raw);
      const fmt = FORMATS.find((f) => f.label === saved.formatLabel);
      if (fmt) setFormat(fmt);
      if (saved.assetName) setAssetName(saved.assetName);
      pendingRestore.current = saved.state;
    } catch (e) {
      // A corrupt draft should not stop the studio opening.
    }
  }, [accountId]);

  useEffect(() => {
    if (!ready || !pendingRestore.current) return;
    const state = pendingRestore.current;
    pendingRestore.current = null;
    applyState(state).then(() => {
      snapshot();
      setNotice('Picked up where you left off. Start new creative to clear it.');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  /** Clear the canvas and the held draft. */
  const startNew = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.remove(...canvas.getObjects());
    canvas.backgroundColor = '#FFFFFF';
    canvas.discardActiveObject();
    canvas.renderAll();

    historyRef.current = [];
    historyIndex.current = -1;
    setCanUndo(false);
    setCanRedo(false);
    setSelected(null);
    setAssetName('');
    setLastPrompt('');
    setPrompt('');

    try {
      sessionStorage.removeItem(draftKey(accountId));
    } catch (e) { /* nothing to clear */ }
    setDraftSaved(false);
    setDraftTooBig(false);
    snapshot();
    flash('Started a new creative.');
  };

  const loadFromLibrary = useCallback(async (assetId) => {
    if (!assetId) return;
    setBusy('library');
    setError(null);
    try {
      const data = await apiGet(`/creative-library/${accountId}/asset/${assetId}/data`);
      await setArtwork(`data:${data.content_type};base64,${data.data}`);
      setAssetName((prev) => prev || `${data.asset_name} v2`);
      flash('Loaded from the library. The original is untouched — saving creates a new asset.');
    } catch (err) {
      setError(err.message || 'Could not load that asset.');
    }
    setBusy(null);
  }, [accountId, setArtwork]);

  // Arriving from the library's "Edit in Studio" button. Loaded once, then the
  // parameter is dropped so a refresh does not overwrite work in progress.
  useEffect(() => {
    const assetId = searchParams.get('asset');
    if (!assetId || !ready || !accountId || loadedFromParam.current) return;
    loadedFromParam.current = true;
    loadFromLibrary(assetId);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, accountId, searchParams]);

  const handleUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setArtwork(reader.result);
    reader.readAsDataURL(file);
  };

  // Zoom is a view setting, not a property of the creative. The canvas element
  // grows and Fabric scales its contents, while the logical coordinate space
  // stays at display size so every position and font size means the same thing
  // at any zoom.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setZoom(zoom);
    canvas.setDimensions({
      width: Math.round(displayW * zoom),
      height: Math.round(displayH * zoom),
    });
    canvas.requestRenderAll();
  }, [zoom, displayW, displayH, ready]);

  /**
   * Run something with the canvas temporarily back at 1:1.
   *
   * Every export path needs this. toDataURL multiplies whatever is on screen,
   * so exporting while zoomed to 200% would produce an asset at twice the
   * intended pixel size — and amending while zoomed would send the model a
   * different image from the one on screen.
   */
  const atNativeZoom = (fn) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const current = canvas.getZoom();
    if (current !== 1) {
      canvas.setZoom(1);
      canvas.setDimensions({ width: displayW, height: displayH });
    }
    let out = null;
    try {
      out = fn();
    } finally {
      if (current !== 1) {
        canvas.setZoom(current);
        canvas.setDimensions({
          width: Math.round(displayW * current),
          height: Math.round(displayH * current),
        });
        canvas.requestRenderAll();
      }
    }
    return out;
  };

  /** The whole canvas as a PNG data URL, at export resolution. */
  const exportCanvas = (multiplier = 1 / scale) => atNativeZoom(() => {
    const canvas = canvasRef.current;
    canvas.discardActiveObject();
    canvas.renderAll();
    return canvas.toDataURL({ format: 'png', multiplier });
  });

  /**
   * Just the artwork, with text, logos and blocks hidden.
   *
   * This is what goes to the model for an amend. Sending the flattened canvas
   * meant the model re-rendered the headline as part of the picture, and
   * generated lettering comes back malformed or missing — the first amend lost
   * the text entirely. Hiding the overlays keeps them as live objects that sit
   * on top of whatever comes back.
   *
   * Exported at full creative resolution. The upload limit is 25MB and a
   * 1080x1920 PNG is a few megabytes, so there is no reason to send the model
   * anything smaller than the asset being made.
   */
  const exportArtworkOnly = () => atNativeZoom(() => {
    const canvas = canvasRef.current;
    const overlays = canvas.getObjects().filter((o) => o.bsRole !== 'artwork');
    // Only the ones this call hides are restored afterwards, so a layer the
    // user chose to hide stays hidden.
    const hidden = overlays.filter((o) => o.visible !== false);

    canvas.discardActiveObject();
    hidden.forEach((o) => { o.visible = false; });
    canvas.renderAll();
    // At the creative's real resolution, not the on-screen size. The canvas is
    // displayed at a few hundred pixels so it fits the page — a story format is
    // 292x520 on screen — and exporting at multiplier 1 sent the model that
    // thumbnail and asked it to redraw from it. Everything came back mush.
    const dataUrl = canvas.toDataURL({ format: 'png', multiplier: 1 / scale });
    hidden.forEach((o) => { o.visible = true; });
    canvas.renderAll();

    return dataUrl;
  });

  const runAI = async (mode) => {
    if (!prompt.trim()) return;
    setBusy('ai');
    setError(null);
    setNotice(null);

    try {
      const body = { prompt: prompt.trim(), mode, aspect_ratio: format.ratio };

      if (mode === 'edit') {
        const hasArtwork = canvasRef.current
          ?.getObjects().some((o) => o.bsRole === 'artwork');
        if (!hasArtwork) {
          throw new Error('There is no image on the canvas to amend. Generate or upload one first.');
        }
        body.source_image = exportArtworkOnly().split(',')[1];
      }

      const result = await apiPost(`/studio/${accountId}/generate`, body);
      await setArtwork(`data:${result.mime_type};base64,${result.image}`);
      setLastPrompt(prompt.trim());

      const brandNote = result.brand_context_applied
        ? 'using this client’s brand guidelines'
        : 'without brand guidelines, as none are configured';
      flash(mode === 'edit'
        ? `Image amended ${brandNote}. Text and logos were left untouched.`
        : `Generated ${brandNote}.`);
    } catch (err) {
      setError(err.message || 'The image could not be produced.');
    }
    setBusy(null);
  };

  const saveToLibrary = async () => {
    if (!assetName.trim()) {
      setError('Give this creative a name before saving it.');
      return;
    }
    setBusy('save');
    setError(null);
    setNotice(null);

    try {
      const dataUrl = exportCanvas();
      const blob = await (await fetch(dataUrl)).blob();

      const formData = new FormData();
      formData.append('file', blob, `${assetName.trim()}.png`);
      formData.append('asset_name', assetName.trim());
      formData.append('campaign_type', campaignType);
      if (lastPrompt) formData.append('source_prompt', lastPrompt);

      const result = await apiPostForm(`/studio/${accountId}/save`, formData);
      flash(result.message);
      apiGet(`/creative-library/${accountId}`)
        .then((data) => setLibrary(data.assets || []))
        .catch(() => {});
    } catch (err) {
      setError(err.message || 'Could not save this creative.');
    }
    setBusy(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Creative is built for one client, with that client's brand.
  if (isAll) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Agentic Studio</h1>
          <p className="page-subtitle">Creative is built for one client at a time</p>
        </div>
        <div className="card">
          <div className="empty-state">
            <h3>Select a client</h3>
            <p>Choose a client from the switcher above to open the studio with their brand.</p>
          </div>
        </div>
      </>
    );
  }

  const brandConfigured = brand.guidelines?.configured;
  const isText = selected?.type === 'textbox';
  const objects = ready && canvasRef.current ? canvasRef.current.getObjects() : [];
  const layers = [...objects].reverse();   // top of the stack first, as drawn
  const imageAssets = library.filter((a) => a.asset_type === 'IMAGE');

  const swatches = (onPick) => (
    <div className="st-swatches">
      {palette.map((c, i) => (
        <button
          key={`${c.hex}-${i}`}
          className="st-swatch"
          title={`${c.name || c.hex}${c.role ? ` — ${c.role}` : ''}`}
          onClick={() => onPick(c.hex)}
          style={{ background: c.hex }}
        />
      ))}
    </div>
  );

  const zoomOut = () => setZoom((z) => {
    const i = ZOOM_STEPS.findIndex((s) => s >= z);
    return ZOOM_STEPS[Math.max(0, (i === -1 ? ZOOM_STEPS.length - 1 : i) - 1)];
  });
  const zoomIn = () => setZoom((z) => {
    const i = ZOOM_STEPS.findIndex((s) => s > z);
    return i === -1 ? ZOOM_STEPS[ZOOM_STEPS.length - 1] : ZOOM_STEPS[i];
  });

  return (
    <>
      <div className="page-header split">
        <div>
          <h1 className="page-title">Agentic Studio</h1>
          <p className="page-subtitle">
            {client ? `${client.name} · ` : ''}
            build and amend creative. Anything saved here goes to the library pending approval.
          </p>
        </div>
        <div className="hdr-actions">
          <button className="btn btn-secondary" onClick={startNew} disabled={!ready}>
            <Icon name="plus" size={14} strokeWidth={2.4} />
            Start new creative
          </button>
        </div>
      </div>

      {error && (
        <div className="banner bad">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{error}</span>
        </div>
      )}

      {!brandConfigured && (
        <div className="banner warn">
          <Icon name="info" size={16} strokeWidth={2.2} />
          <span>
            <b>No brand guidelines yet.</b> The studio is using generic defaults. Set them under
            Brand Guidelines and they appear here automatically — palette, typefaces, logos and
            safe area.
          </span>
        </div>
      )}

      <input
        ref={fileInputRef} type="file" style={{ display: 'none' }}
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={(e) => { handleUpload(e.target.files[0]); e.target.value = ''; }}
      />

      <div className="st-grid">

        {/* ── Left rail: layers, adding, colour ── */}
        <aside className="st-rail">
          <Panel title="Layers" icon="layers" tag={layers.length ? `${layers.length}` : null}>
            {layers.length === 0 ? (
              <div className="st-empty-note">Nothing on the canvas yet.</div>
            ) : (
              <div className="st-layers">
                {layers.map((obj, i) => {
                  const active = selected === obj;
                  const hidden = obj.visible === false;
                  return (
                    <div
                      key={`${obj.bsRole || 'obj'}-${i}`}
                      className={`st-layer${active ? ' on' : ''}${hidden ? ' off' : ''}`}
                    >
                      <button
                        type="button"
                        className="st-layer-pick"
                        onClick={() => !hidden && selectObject(obj)}
                        disabled={hidden}
                        title={hidden ? 'Hidden — show it to select it' : `Select ${layerLabel(obj)}`}
                      >
                        <Icon name={ROLE_ICON[obj.bsRole] || 'square'} size={13} strokeWidth={2.1} />
                        <span className="st-layer-name">{layerLabel(obj)}</span>
                      </button>
                      <span className="st-layer-acts">
                        <button
                          className="st-icon"
                          title="Bring forward"
                          disabled={i === 0}
                          onClick={(e) => { e.stopPropagation(); raiseObject(obj); }}
                        >
                          <Icon name="up" size={13} strokeWidth={2.4} />
                        </button>
                        <button
                          className="st-icon"
                          title="Send backward"
                          disabled={i === layers.length - 1}
                          onClick={(e) => { e.stopPropagation(); lowerObject(obj); }}
                        >
                          <Icon name="down" size={13} strokeWidth={2.4} />
                        </button>
                        <button
                          className="st-icon"
                          title={hidden ? 'Show this layer' : 'Hide this layer'}
                          onClick={(e) => { e.stopPropagation(); toggleVisible(obj); }}
                        >
                          <Icon name={hidden ? 'hide' : 'eye'} size={13} strokeWidth={2.1} />
                        </button>
                        <button
                          className="st-icon danger"
                          title="Delete this layer"
                          onClick={(e) => { e.stopPropagation(); removeObject(obj); }}
                        >
                          <Icon name="trash" size={13} strokeWidth={2.1} />
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>

          <div className="st-divider" />

          <Panel title="Start from" icon="image">
            <div className="st-stack">
              <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
                Upload an image
              </button>
              <select
                className="form-select"
                value=""
                onChange={(e) => loadFromLibrary(e.target.value)}
                disabled={busy === 'library' || !imageAssets.length}
              >
                <option value="">
                  {busy === 'library'
                    ? 'Loading…'
                    : imageAssets.length ? 'From creative library…' : 'Library is empty'}
                </option>
                {imageAssets.map((a) => (
                  <option key={a.asset_id} value={a.asset_id}>{a.asset_name}</option>
                ))}
              </select>
            </div>
          </Panel>

          <Panel title="Add" icon="plus">
            <div className="st-stack">
              <button className="btn btn-secondary" onClick={() => addText('heading')} disabled={!ready}>
                Headline
              </button>
              <button className="btn btn-secondary" onClick={() => addText('body')} disabled={!ready}>
                Body text
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => addColourBlock(palette[0]?.hex || '#0f2e24')}
                disabled={!ready}
              >
                Colour panel
              </button>
            </div>
          </Panel>

          {brand.logos?.length > 0 && (
            <Panel title="Logos" icon="shield" hint="From Brand Guidelines. Click to place.">
              <div className="st-logos">
                {brand.logos.map((logo) => (
                  <button
                    key={logo.brand_asset_id}
                    className="st-logo"
                    onClick={() => addLogo(logo)}
                    title={`Add ${logo.label}${logo.variant ? ` — ${logo.variant}` : ''}`}
                    disabled={!ready}
                  >
                    {logo.url
                      ? <img src={logo.url} alt={logo.label} />
                      : <span className="none">unavailable</span>}
                  </button>
                ))}
              </div>
            </Panel>
          )}

          <Panel
            title="Colours"
            icon="cards"
            tag={paletteIsBrand ? 'brand' : 'defaults'}
            hint={
              paletteIsBrand
                ? 'Click to recolour the selection, or to drop a panel when nothing is selected.'
                : 'No brand palette for this client yet, so these are the dashboard defaults.'
            }
          >
            {swatches(applyColour)}
            <label className="st-anycolour">
              <input
                type="color"
                className="st-colourwell"
                onChange={(e) => applyColour(e.target.value)}
              />
              Any colour
            </label>
          </Panel>
        </aside>

        {/* ── Centre: the artboard ── */}
        <div className="st-stage">
          <div className="st-bar">
            <div className="st-formats" role="group" aria-label="Placement">
              {FORMATS.map((f) => (
                <button
                  key={f.label}
                  className="st-fmt"
                  aria-pressed={format.label === f.label}
                  onClick={() => setFormat(f)}
                  title={`${f.sub} · ${f.w}×${f.h}`}
                >
                  <FormatGlyph w={f.w} h={f.h} />
                  {f.label}
                  <span className="st-fmt-dims">{f.ratio}</span>
                </button>
              ))}
            </div>

            <span className="st-bar-spacer" />

            <button className="st-tool" onClick={undo} disabled={!canUndo} title="Undo the last change">
              <Icon name="undo" size={14} strokeWidth={2.2} />
            </button>
            <button className="st-tool" onClick={redo} disabled={!canRedo} title="Redo">
              <Icon name="redo" size={14} strokeWidth={2.2} />
            </button>

            <span className="st-bar-sep" />

            <button className="st-tool" onClick={zoomOut} disabled={zoom <= ZOOM_STEPS[0]} title="Zoom out">
              <Icon name="minus" size={14} strokeWidth={2.6} />
            </button>
            <span className="st-zoom">{Math.round(zoom * 100)}%</span>
            <button
              className="st-tool"
              onClick={zoomIn}
              disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
              title="Zoom in"
            >
              <Icon name="plus" size={14} strokeWidth={2.6} />
            </button>
            <button className="st-tool" onClick={() => setZoom(1)} disabled={zoom === 1} title="Back to 100%">
              Fit
            </button>
          </div>

          <div className="st-board-wrap">
            <div
              className="st-board"
              style={{ width: Math.round(displayW * zoom), height: Math.round(displayH * zoom) }}
            >
              <canvas ref={canvasElRef} />

              {/* The safe area, drawn over the canvas rather than on it, so it
                  guides the layout without appearing in the export. */}
              {safeMarginPct > 0 && (
                <div className="st-safe" aria-hidden="true" style={{ inset: `${safeMarginPct}%` }} />
              )}

              {ready && objects.length === 0 && (
                <div className="st-empty">
                  <Icon name="frame" size={26} strokeWidth={1.5} />
                  <b>An empty {format.label.toLowerCase()} artboard</b>
                  <span>
                    Describe a picture and generate it, upload one, or start from the creative
                    library — then lay type over the top.
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="st-meta">
            <span className="dims">{format.w}×{format.h}</span>
            {safeMarginPct > 0 && <span>Dashed line is the {safeMarginPct}% safe area</span>}
            <span>Delete removes the selection</span>
            {draftTooBig ? (
              <span className="warn">
                <Icon name="alert" size={12} strokeWidth={2.4} /> Too large to hold — this canvas
                will not survive leaving the page
              </span>
            ) : draftSaved ? (
              <span className="held">
                <Icon name="check" size={12} strokeWidth={2.6} /> Held for this session
              </span>
            ) : null}
          </div>
        </div>

        {/* ── Right rail: properties, AI, save ── */}
        <aside className="st-rail st-right">

          {/* Properties for whatever is selected. Present only when something
              is, so the rail is not full of disabled controls. */}
          {selected ? (
            <Panel
              title={isText ? 'Text' : ROLE_NAME[selected.bsRole] || 'Element'}
              icon={ROLE_ICON[selected.bsRole] || 'square'}
            >
              {isText && (
                <>
                  <div className="st-field">
                    <span className="st-field-l">Colour</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {swatches((hex) => updateSelected({ fill: hex }))}
                      <input
                        type="color"
                        className="st-colourwell"
                        value={typeof selected.fill === 'string' ? selected.fill : '#000000'}
                        onChange={(e) => updateSelected({ fill: e.target.value })}
                        title="Any colour"
                      />
                    </div>
                  </div>

                  <label className="st-field">
                    <span className="st-field-l">Font</span>
                    <select
                      className="form-select"
                      value={selected.fontFamily || ''}
                      onChange={(e) => updateSelected({ fontFamily: e.target.value })}
                    >
                      {fontChoices.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </label>

                  <div className="st-field st-two">
                    <NumField
                      label="Size" min={6} max={400}
                      value={Math.round(selected.fontSize || 0)}
                      onChange={(v) => updateSelected({ fontSize: v || 12 })}
                    />
                    <label style={{ display: 'block' }}>
                      <span className="st-field-l">Weight</span>
                      <select
                        className="form-select"
                        value={selected.fontWeight || 400}
                        onChange={(e) => updateSelected({ fontWeight: Number(e.target.value) })}
                      >
                        {FONT_WEIGHTS.map((w) => <option key={w} value={w}>{w}</option>)}
                      </select>
                    </label>
                  </div>

                  <div className="st-field st-two">
                    <NumField
                      label="Line height" step={0.05} min={0.7} max={3}
                      value={Number((selected.lineHeight ?? 1.16).toFixed(2))}
                      onChange={(v) => updateSelected({ lineHeight: v || 1.16 })}
                    />
                    {/* charSpacing is in thousandths of an em in Fabric, which
                        is not a number anyone thinks in — shown as a plain
                        nudge value and scaled here. */}
                    <NumField
                      label="Letter spacing" step={1} min={-50} max={200}
                      value={Math.round((selected.charSpacing || 0) / 10)}
                      onChange={(v) => updateSelected({ charSpacing: (v || 0) * 10 })}
                    />
                  </div>

                  <div className="st-field">
                    <span className="st-field-l">Align</span>
                    <div className="st-align">
                      {['left', 'center', 'right'].map((a) => (
                        <button
                          key={a}
                          aria-pressed={selected.textAlign === a}
                          onClick={() => updateSelected({ textAlign: a })}
                        >
                          {a === 'center' ? 'centre' : a}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {!isText && selected.bsRole !== 'artwork' && selected.bsRole !== 'logo' && (
                <div className="st-field">
                  <span className="st-field-l">Fill</span>
                  {swatches((hex) => updateSelected({ fill: hex }))}
                </div>
              )}

              <div className="st-stack">
                <button className="btn btn-secondary" onClick={nudgeInsideSafeArea}>
                  Nudge inside safe area
                </button>
                <button className="btn btn-reject" onClick={() => removeObject(selected)}>
                  Delete this element
                </button>
              </div>
            </Panel>
          ) : (
            <Panel title="Selection" icon="target">
              <div className="st-empty-note">
                Nothing selected. Click an element on the canvas, or a layer on the left, to change
                its colour, size, spacing or alignment.
              </div>
            </Panel>
          )}

          <div className="st-divider" />

          <Panel
            title="AI"
            icon="spark"
            tag={brandConfigured ? 'brand on' : 'no brand'}
            hint={
              brandConfigured
                ? 'Brand colours, typefaces and tone are sent with every request.'
                : 'No brand guidelines are configured, so prompts are sent without them.'
            }
          >
            <div className="st-ai">
              <div className="st-ai-top">
                <Icon name="spark" size={16} strokeWidth={1.9} />
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  maxLength={1200}
                  placeholder="A bright summer beach scene, low angle, lots of empty sky for text…"
                  aria-label="Describe the image you want"
                />
              </div>
            </div>
            <div className="st-ai-btns">
              <button
                className="btn btn-brass"
                onClick={() => runAI('generate')}
                disabled={busy === 'ai' || !prompt.trim()}
              >
                {busy === 'ai' ? 'Working…' : 'Generate new image'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => runAI('edit')}
                disabled={busy === 'ai' || !prompt.trim() || !ready}
              >
                Amend the image
              </button>
            </div>
            <p className="st-ai-note">
              Amending sends the picture only. Text and logos stay as they are — they are never
              sent to the model, because generated lettering comes back malformed.
            </p>
          </Panel>

          <div className="st-divider" />

          <Panel title="Save" icon="download">
            <div className="st-save">
              <label className="st-field">
                <span className="st-field-l">Asset name</span>
                <input
                  className="form-input"
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. Summer Story 1080x1920"
                />
              </label>
              <label className="st-field">
                <span className="st-field-l">Campaign type</span>
                <select
                  className="form-select"
                  value={campaignType}
                  onChange={(e) => setCampaignType(e.target.value)}
                >
                  <option value="OUTCOME_LEADS">Leads</option>
                  <option value="OUTCOME_TRAFFIC">Traffic</option>
                  <option value="OUTCOME_AWARENESS">Awareness</option>
                  <option value="OUTCOME_SALES">Sales</option>
                </select>
              </label>
              <button
                className="btn btn-approve"
                onClick={saveToLibrary}
                disabled={busy === 'save' || !assetName.trim() || !ready}
              >
                {busy === 'save' ? 'Saving…' : 'Push to Creative Library'}
              </button>
              <div className="st-save-note">
                <Icon name="shield" size={13} strokeWidth={2.2} />
                <span>
                  Saved as pending approval. It is not sent to Meta, and agents cannot use it,
                  until someone approves it in the library.
                </span>
              </div>
            </div>
          </Panel>
        </aside>
      </div>

      {notice && (
        <div className="toast-host">
          <div className="toast">
            <Icon name="check" size={16} strokeWidth={2.6} />
            {notice}
          </div>
        </div>
      )}
    </>
  );
}